// @flow @jsx h

import { Component, h } from 'preact'
import styles from './index.css'
import 'leaflet/dist/leaflet.css'
import type { SectionType } from '../../domain/index'

const imageWidth = 16384
const tileWidth = 256

export type Overlay = {
  url: string,
  id: string,
  bounds: string
}

export type Label = {
  text: string,
  position: string
}

type Props = {
  section: SectionType,
  overlays: Overlay[],
  labels: Label[],
  fullWidth: boolean
}

function toArray (str: string) {
  return (str || '').split(',')
    .map(str => str.trim())
    .map(str => parseInt(str))
}

function toTileCoordinates (str: string) {
  const [x1, y1, x2, y2] = toArray(str).map(coord => coord / imageWidth * tileWidth)
  return [[-1 * y1, x1], [-1 * y2, x2]]
}

export class Map extends Component<Props> {
  map: any
  L: any

  componentDidMount () {
    const L = require('./leafletWrapper').L
    this.L = L
    const map = L.map('leafletMap', {
      crs: L.CRS.Simple,
      zoomControl: false,
      dragging: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      zoomSnap: false,
      minZoom: -1,
      tap: false,
      maxZoom: 6
    })
    this.map = map

    let minZoom
    if (this.props.fullWidth) {
      minZoom = window.innerWidth < 720 ? 3 : 4
    } else {
      minZoom = 3
    }

    map.createPane('BackgroundImage')
    map.createPane('Tiles')
    L.imageOverlay('https://static.nrk.no/dh/gfx/2018/orderud/tidslinje-v6c/tidslinje-v6-base-c.jpg', [[0, 0], [-256, 256]], {
      pane: 'Tiles'
    }).addTo(this.map)

    L.tileLayer('https://static.nrk.no/dh/gfx/2018/orderud/tidslinje-v6c/{z}/{x}_{y}.jpg', {
      edgeBufferTiles: 1,
      pane: 'Tiles',
      bounds: [[0, 0], [-256, 256]],
      minZoom,
      maxZoom: 6,
      detectRetina: true,
      updateWhenZooming: false,
      updateWhenIdle: true
    }).addTo(this.map)

    const { center, zoom } = this.getBoundsInside(this.map, [[0, 0], [-256, 256]])
    map.setView(center, zoom)
  }

  componentDidUpdate () {
    const { section } = this.props
    if (section) {
      if (section.bounds) {
        const duration = section.duration ? parseFloat(section.duration) : 1.4
        const bounds = toTileCoordinates(section.bounds)
        const align = section.type === 'normal' ? section.align : 'none'
        const { center, zoom } = this.getBounds(this.map, bounds, align, section.inside === 'true')
        if (center && zoom && zoom > -1) {
          this.map.flyTo(center, zoom, { duration, easeLinearity: 0.8 })
        }
      }
    }
  }

  getBoundsInside (map: any, bounds: any) {
    const L = this.L
    bounds = L.latLngBounds(bounds)
    const zoom = map.getBoundsZoom(bounds, true)

    const swPoint = map.project(bounds.getSouthWest(), zoom)
    const nePoint = map.project(bounds.getNorthEast(), zoom)
    const center = map.unproject(swPoint.add(nePoint).divideBy(2), zoom)

    return { center, zoom }
  }

  getBounds (map: any, bounds: any, align?: string, inside: boolean) {
    const L = this.L
    bounds = L.latLngBounds(bounds)
    const zoom = map.getBoundsZoom(bounds, inside)
    const swPoint = map.project(bounds.getSouthWest(), zoom)
    const nePoint = map.project(bounds.getNorthEast(), zoom)
    const centerPoint = swPoint.add(nePoint).divideBy(2)

    const boundsSize = map.unproject(nePoint.subtract(swPoint), zoom)
    const size = map.getSize()
    if (size.x === 0 || size.y === 0) {
      return {
        center: map.unproject(centerPoint),
        zoom
      }
    } else if (size.x === size.y) {
      const offsetPoint = map.project(L.latLng([-0.1 * boundsSize.lat, 0]), zoom)
      return {
        center: map.unproject(centerPoint.add(offsetPoint), zoom),
        zoom: zoom
      }
    } else if (size.y >= size.x) {
      const scale = size.y / size.x
      let offset = 0
      if (align !== 'none') {
        const remainingHeight = (scale - 1.0) * boundsSize.lat
        offset = -0.4 * remainingHeight
      }

      const offsetPoint = map.project(L.latLng([offset, 0]), zoom)
      return {
        center: map.unproject(centerPoint.add(offsetPoint), zoom),
        zoom: zoom
      }
    } else {
      const scale = size.x / size.y

      const remainingHeight = (scale - 1.0) * boundsSize.lat
      let offset = 0
      if (align === 'left') {
        offset = -0.25 * remainingHeight
      } else if (align === 'right') {
        offset = 0.25 * remainingHeight
      }

      const offsetPoint = map.project(L.latLng([0, offset]), zoom)
      return {
        center: map.unproject(centerPoint.add(offsetPoint), zoom),
        zoom: zoom
      }
    }
  }

  render () {
    return <div id='leafletMap' class={styles.map} />
  }
}
